<template>
  <div v-if="hasAccess" class="tutor-voice-row">
    <div class="voice-row-label">{{ $t('account.tutorVoice.label') }}</div>
    <div class="voice-row-value">
      <div class="voice-options" role="radiogroup" :aria-label="$t('account.tutorVoice.label')">
        <div
          v-for="voice in voices"
          :key="voice.id"
          class="voice-option"
          :class="{ active: selectedVoice === voice.id }"
        >
          <button
            type="button"
            class="voice-select"
            role="radio"
            :aria-checked="selectedVoice === voice.id"
            @click="selectVoice(voice.id)"
          >
            <span class="voice-name">{{ voice.name }}</span>
            <span class="voice-descriptor">{{ $t(voice.descriptorKey) }}</span>
          </button>
          <button
            type="button"
            class="voice-preview-btn"
            :aria-label="$t('account.tutorVoice.playPreview', { voice: voice.name })"
            @click="playPreview(voice.id)"
          >
            <font-awesome-icon :icon="playingVoiceId === voice.id ? 'pause' : 'play'" />
          </button>
        </div>
      </div>
      <p class="voice-note">{{ $t('account.tutorVoice.forwardNote') }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import { getDatabase, ref as dbRef, get, set } from 'firebase/database';
import { useI18n } from 'vue-i18n';
import { authStore } from '@/store/auth';
import { useToast } from '@/composables/useToast.js';
import { featureFlags } from '@/store/featureFlags.js';

// Voice names are product proper nouns and are never translated. The IDs are
// the only thing stored or sent anywhere; the backend registry maps them to
// provider speakers.
const VOICES = [
  { id: 'prosa', name: 'Prosa', descriptorKey: 'account.tutorVoice.prosaDescriptor' },
  { id: 'verso', name: 'Verso', descriptorKey: 'account.tutorVoice.versoDescriptor' },
];
const DEFAULT_VOICE_ID = 'prosa';

export default {
  name: 'TutorVoicePicker',
  props: {
    langCode: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();
    const { showToast } = useToast();
    const hasAccess = computed(() => featureFlags.tutorVoice);
    const selectedVoice = ref(DEFAULT_VOICE_ID);
    const playingVoiceId = ref(null);
    let currentAudio = null;
    // Guards the async initial read: bumped by every new load AND by a user
    // selection, so a slow get() can never overwrite a choice the user just
    // made (or a newer language's load).
    let loadToken = 0;

    const voicePath = () =>
      `users/${authStore.getUserId()}/languagePreferences/${props.langCode}/tutorVoice`;

    const loadSelection = async () => {
      const token = ++loadToken;
      try {
        const snap = await get(dbRef(getDatabase(), voicePath()));
        if (token !== loadToken) return; // stale: user picked or language changed
        const stored = snap.val();
        selectedVoice.value = VOICES.some((v) => v.id === stored)
          ? stored
          : DEFAULT_VOICE_ID;
      } catch (error) {
        if (token !== loadToken) return;
        console.error('Error loading tutor voice:', error);
        selectedVoice.value = DEFAULT_VOICE_ID;
      }
    };
    watch(() => props.langCode, loadSelection, { immediate: true });

    const selectVoice = async (voiceId) => {
      if (voiceId === selectedVoice.value) return;
      // Token does double duty: invalidates any in-flight initial read AND
      // marks this write, so a failure from a superseded pick is dropped
      // instead of clobbering a newer selection with a stale revert.
      const token = ++loadToken;
      selectedVoice.value = voiceId;
      try {
        await set(dbRef(getDatabase(), voicePath()), voiceId);
      } catch (error) {
        if (token !== loadToken) return; // a newer pick owns the UI now
        console.error('Error saving tutor voice:', error);
        showToast(t('account.tutorVoice.saveError'), 'error');
        // Resync from the server rather than guessing at the pre-click value:
        // after rapid toggles the DB truth may not be `previous`.
        await loadSelection();
      }
    };

    const stopPreview = () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio = null;
      }
      playingVoiceId.value = null;
    };

    const playPreview = (voiceId) => {
      const wasPlaying = playingVoiceId.value === voiceId;
      stopPreview();
      if (wasPlaying) return;
      const previewAudio = new Audio(`/voice-previews/${props.langCode}-${voiceId}.mp3`);
      const failQuietly = () => {
        // A superseded audio rejects play() with AbortError when the user
        // pauses or switches previews; that is intentional, not an error.
        if (currentAudio !== previewAudio) return;
        stopPreview();
        showToast(t('account.tutorVoice.previewError'), 'error');
      };
      previewAudio.addEventListener('ended', () => {
        if (currentAudio === previewAudio) stopPreview();
      });
      previewAudio.addEventListener('error', failQuietly);
      currentAudio = previewAudio;
      playingVoiceId.value = voiceId;
      previewAudio.play().catch(failQuietly);
    };

    onUnmounted(stopPreview);

    return {
      hasAccess,
      voices: VOICES,
      selectedVoice,
      playingVoiceId,
      selectVoice,
      playPreview,
    };
  },
};
</script>

<style scoped>
/* Mirrors AccountView's .info-row / .info-label look (those styles are scoped
   to AccountView and cannot reach into this component). Keep the desktop
   breakpoint in sync with AccountView's .info-row media query. */
.tutor-voice-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--al-border);
  min-height: 44px;
}

.voice-row-label {
  font-weight: 600;
  color: var(--al-text-muted);
  margin-bottom: 0.5rem;
}

@media (min-width: 768px) {
  .tutor-voice-row {
    flex-direction: row;
  }

  .voice-row-label {
    flex: 0 0 40%;
    margin-bottom: 0;
  }
}

.voice-options {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  /* Cap the ROW, not the card (2 x 16rem + gap). Capping the card instead
     would leave dead space on the trailing edge once the cards wrap, since a
     wrapped card would stop growing at 16rem instead of filling its row. */
  max-width: 32.75rem;
}

.voice-option {
  display: flex;
  align-items: stretch;
  /* Equal flex-grow shares split the row by count, not content, so the two
     cards are always the same width regardless of descriptor length or locale.
     The 10rem basis (not 0) keeps wrapping alive: when two cards + gap can't
     fit (narrow phones, long locales), each wraps to its own full-width row
     instead of squeezing side by side. */
  flex: 1 1 10rem;
  min-width: 0;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-sm);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.voice-option.active {
  border-color: var(--al-primary);
  box-shadow: 0 0 0 1px var(--al-primary);
}

.voice-select {
  display: flex;
  /* Absorb the card's extra width so the preview button stays pinned right. */
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  /* Buttons center their text by default, which only became visible once the
     card widened enough for long descriptors (e.g. Telugu) to wrap. `start`
     rather than `left` so the label side follows the script direction. */
  text-align: start;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  min-height: 48px;
}

.voice-name {
  font-weight: 600;
  color: var(--al-text);
}

.voice-option.active .voice-name {
  color: var(--al-primary-hover);
}

.voice-descriptor {
  font-size: 0.8125rem;
  color: var(--al-text-muted);
}

.voice-preview-btn {
  border: none;
  border-left: 1px solid var(--al-border);
  background: transparent;
  color: var(--al-text-muted);
  padding: 0 0.75rem;
  cursor: pointer;
  min-width: 44px;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.voice-preview-btn:hover {
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
}

.voice-note {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: var(--al-text-muted);
}
</style>
